import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import CargomaticTab from 'components/v2/navs/Tab';
import { useTranslation } from 'react-i18next';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { useParams } from 'react-router-dom';
import store from 'stores/CustomerDetailsStore';
import { fetchCreditCards, getCustomerV2, updateShipper, updateShipperInvoiceDocumentPreferences, } from 'utils/CustomersAPIUtils';
import Spinner from 'components/ui/Spinner';
import CustomersActionCreators from 'actions/CustomersActionCreators';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { showToast } from 'stores/ToastStore';
import { onCreateCreditCard } from 'utils/StripeAPIUtils';
import UserActionCreators from 'actions/UserActionCreators';
import DrayageBillReadyEventSettings from 'components/modules/user/shipper/billingv2/drayage/DrayageBillReadyEventsSetting';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import ShipperSettings from '../ShipperSettings';
import DrayageInvoiceDocumentPreferences from './drayage/DrayageInvoiceDocumentPreferences';
import DrayageInvoiceSettings from './drayage/DrayageInvoiceSettings';
import AccountingSettings from './AccountingSettings';
import PaymentTerms from './PaymentTerms';
import BillingInfo from './BillingInfo';
import CreditCard from './CreditCard';
import Invoicing from './Invoicing';
import ShorthaulInvoiceSettings from './shorthaul/ShorthaulInvoiceSettings';
const BillingV2 = () => {
    const { t } = useTranslation();
    const { id: userId } = useParams();
    const [user, setUser] = useState({});
    const [cards, setCards] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // fetch shipper
    const fetchUser = async () => {
        setIsLoading(true);
        const user = await getCustomerV2(userId);
        setUser(user);
        setIsLoading(false);
    };
    // update shipper
    const onSaveShipper = async (updates = {}) => {
        const payload = Object.assign({ _id: userId }, updates);
        await updateShipper(userId, payload);
    };
    const setStripeKey = () => {
        // Set Stripe keys to allow billing actions
        Stripe.setPublishableKey(STRIPE_KEY);
    };
    const fetchCards = async (userId, cardId) => {
        try {
            const response = await fetchCreditCards(userId, cardId);
            const creditCards = response || [];
            setCards(creditCards);
            showToast(t('CREDIT_CARD_UPDATED'));
        }
        catch (e) {
            showToast(t('CREDIT_CARD_NOT_UPDATED'), '', 'error');
        }
    };
    // update invoice Document Preferences
    const onSaveDocumentPreferences = async (payload) => {
        await updateShipperInvoiceDocumentPreferences(userId, payload);
    };
    const onSaveCreditCard = async (updates = {}) => {
        const payload = Object.assign({ userId }, updates);
        const response = await onCreateCreditCard(payload);
        if (response) {
            fetchCards(userId, response.id);
        }
    };
    useEffect(() => {
        store.addChangeListener(fetchUser);
        CustomersActionCreators.getCustomer(userId);
        UserActionCreators.getAdminUsers({ active: true });
        return () => {
            store.removeChangeListener(fetchUser);
        };
    }, []);
    const { details: shipperUser = {} } = store;
    const tabs = [
        {
            slug: 'general',
            name: t('GENERAL'),
        },
        {
            slug: 'drayage',
            name: t('DRAYAGE'),
        },
        {
            slug: 'shorthaul',
            name: t('SHORTHAUL'),
        },
    ];
    const breadcrumbs = {
        pages: [
            {
                name: (user === null || user === void 0 ? void 0 : user.company_name) || t('SHIPPER'),
                href: `/users/${userId}`,
                current: false,
            },
            {
                name: t('BILLING'),
                href: `/users/${userId}/settings/billing`,
                current: true,
            },
        ],
    };
    const renderSidebar = () => {
        return React.createElement(ShipperSettings, { userId: userId, activePage: "billing" });
    };
    const getInvoicingData = () => {
        return {
            black_and_white_invoices: user === null || user === void 0 ? void 0 : user.black_and_white_invoices,
            userId: userId,
        };
    };
    const getBillingInfoData = () => {
        const { billing_address = {} } = user;
        return billing_address;
    };
    const getAccountingData = () => {
        const { can_pay_manually, net_suite_id, accounting = {}, crm_mapping_id, } = user;
        return {
            can_pay_manually,
            net_suite_id,
            reason: accounting === null || accounting === void 0 ? void 0 : accounting.reason,
            crm_mapping_id,
        };
    };
    const getPaymentTermsData = () => {
        const { drayage_payment_method, drayage_terms, shorthaul_payment_method, shorthaul_terms, stripe_customer_id, credit_limit, } = user;
        return {
            drayage_payment_method,
            drayage_terms,
            shorthaul_payment_method,
            shorthaul_terms,
            hasCreditCardOnFile: !!stripe_customer_id,
            credit_limit,
        };
    };
    const getCreditCardData = (creditCards = []) => {
        const { funding_sources = [], stripe_customer_id } = shipperUser;
        const [creditCardDetails = {}] = creditCards.length
            ? creditCards
            : funding_sources;
        const { brand, last4, exp_month: expiry_month, exp_year: expiry_year, } = creditCardDetails;
        return {
            brand,
            last4,
            expiry_month: expiry_month ? expiry_month.toString() : '',
            expiry_year,
            stripeId: stripe_customer_id,
        };
    };
    const getDrayageInvoiceSettingsData = () => {
        const { drayage_invoice_branding, group_like_invoice_items, drayage_blling_time_frame, invoice_id_settings, invoice_address_settings, _id, invoice_preferences: { page_setup = [] } = {}, } = user;
        const invoiceSetting = invoice_id_settings === 'suffix'
            ? 'PREVIOUS_ID_WITH_SUFFIX'
            : 'UNIQUE_ID';
        return {
            drayage_invoice_branding,
            group_like_invoice_items: group_like_invoice_items ? t('YES') : t('NO'),
            drayage_blling_time_frame,
            invoice_id_settings: t(invoiceSetting),
            invoice_address_settings,
            userId: _id,
            page_setup,
        };
    };
    const getShorthaulInvoiceSettingsData = () => {
        const { invoice_schedule } = user;
        return { invoice_schedule };
    };
    const getInvoiceDocumentPreferencesData = () => {
        const { invoice_document_preferences = [] } = user || {};
        return invoice_document_preferences;
    };
    const renderTabs = () => {
        return (React.createElement(Tab.Group, null,
            React.createElement(Tab.List, null,
                React.createElement("div", { className: "space-x-4" }, tabs.map((tab) => (React.createElement(CargomaticTab, { tab: tab }))))),
            React.createElement(Tab.Panels, { className: "mt-6" }, isLoading ? (React.createElement(Spinner, { showMessage: false })) : (React.createElement("div", null,
                React.createElement(Tab.Panel, null,
                    React.createElement("div", { className: "lg:grid lg:grid-cols-2 lg:gap-4" },
                        React.createElement(AccountingSettings, { data: getAccountingData(), className: "mb-4", onUpdateShipper: onSaveShipper }),
                        React.createElement(PaymentTerms, { data: getPaymentTermsData(), className: "mb-4", onUpdateShipper: onSaveShipper }),
                        React.createElement(AuthorizationWrapper, { excludedRegions: [REGIONS.AP] },
                            React.createElement(BillingInfo, { data: getBillingInfoData(), className: "mb-4", onUpdateShipper: onSaveShipper }),
                            React.createElement(CreditCard, { data: getCreditCardData(cards), className: "mb-4", onUpdateShipper: onSaveCreditCard })),
                        React.createElement(Invoicing, { data: getInvoicingData(), onUpdateShipper: onSaveShipper, className: "mb-4" }))),
                React.createElement(Tab.Panel, null,
                    React.createElement("div", { className: "lg:grid lg:grid-cols-2 lg:gap-4" },
                        React.createElement(DrayageInvoiceDocumentPreferences, { data: getInvoiceDocumentPreferencesData(), onUpdateShipper: onSaveDocumentPreferences, className: "mb-4" }),
                        React.createElement(DrayageInvoiceSettings, { data: getDrayageInvoiceSettingsData(), onUpdateShipper: onSaveShipper }),
                        featureEnabled(CONFIGURED_FEATURES.SSR) && (React.createElement(DrayageBillReadyEventSettings, { userId: userId })))),
                React.createElement(Tab.Panel, null,
                    React.createElement("div", { className: "lg:grid lg:grid-cols-2 lg:gap-4" },
                        React.createElement(ShorthaulInvoiceSettings, { data: getShorthaulInvoiceSettingsData(), onUpdateShipper: onSaveShipper }))))))));
    };
    return (React.createElement(LayoutWithSidebar, { title: t('BILLING_SETTINGS'), breadcrumbs: breadcrumbs, sidebar: renderSidebar, className: "py-4" }, renderTabs()));
};
export default BillingV2;
